import {
  type ComputedRef,
  computed,
  getCurrentScope,
  type MaybeRefOrGetter,
  onScopeDispose,
  type Ref,
  readonly,
  ref,
  toValue,
  watch,
} from 'vue'
import { getToken } from '@/utils'

export interface WebSocketOptions {
  uri?: MaybeRefOrGetter<string | undefined> // 支持响应式 props
  did?: MaybeRefOrGetter<number | undefined> // 支持响应式 props
  token?: string | (() => string) // token 可以作为字符串或 getter 函数传入，提高可测试性和灵活性
  maxReconnect?: number // 最大重连次数，-1 表示无限重连
  heartbeatInterval?: number // 心跳间隔时间（毫秒）
  inactivityTimeout?: number // 无消息超时时间（毫秒）
  reconnectDelay?: number // 重连延迟时间（毫秒）
}

/** Hook 内部触发的消息类型（无消息超时、达到最大重连次数等） */
export type WebSocketInternalMessage = { type: 'inactivity_timeout' } | { type: 'max_reconnect_reached' }

/** 服务端下发的消息为任意 JSON，可与内部类型联合使用 */
export type WebSocketMessage = Record<string, unknown> | WebSocketInternalMessage

export interface WebSocketCallbacks {
  onOpen?: () => void
  onError?: (event: Event) => void
  onClose?: (event: CloseEvent) => void
  onMessage?: (data: WebSocketMessage) => void
}

/** WebSocket 就绪状态（与 WebSocket 常量一致） */
export type WebSocketReadyState =
  | typeof WebSocket.CONNECTING
  | typeof WebSocket.OPEN
  | typeof WebSocket.CLOSING
  | typeof WebSocket.CLOSED

/** useWebSocket 返回值类型 */
export interface UseWebSocketReturn {
  /** 当前 WebSocket 实例（只读） */
  webSocket: Readonly<Ref<WebSocket | null>>
  /** 当前连接状态 */
  readyState: ComputedRef<WebSocketReadyState>
  /** 是否已连接 */
  isConnected: ComputedRef<boolean>
  /** 已重连次数（只读） */
  reconnectTime: Readonly<Ref<number>>
  /** 初始化/建立连接 */
  initWebSocket: () => void
  /** 触发重连 */
  reconnect: () => void
  /** 关闭连接 */
  close: () => void
  /** 发送消息，未连接时返回 false */
  send: (data: string | object) => boolean
  /** 启动心跳（内部使用） */
  startHeartbeat: () => void
  /** 停止心跳（内部使用） */
  stopHeartbeat: () => void
}

/**
 * WebSocket Composable
 * 提供 WebSocket 连接、重连、心跳等核心功能
 */
export function useWebSocket(options: WebSocketOptions = {}, callbacks: WebSocketCallbacks = {}): UseWebSocketReturn {
  const {
    uri,
    did,
    maxReconnect = 4,
    heartbeatInterval = 10 * 1000, // 默认 10 秒
    inactivityTimeout = 5 * 60 * 1000, // 默认 5 分钟
    reconnectDelay = 2000, // 默认 2 秒
  } = options

  // WebSocket 实例
  const webSocket = ref<WebSocket | null>(null)

  // 重连相关状态
  const lockReconnect = ref(false)
  const reconnectTime = ref(0)

  // 定时器引用
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let inactivityTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * 清除心跳定时器
   */
  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  /**
   * 启动心跳
   */
  const startHeartbeat = () => {
    stopHeartbeat()
    heartbeatTimer = setInterval(() => {
      if (webSocket.value && webSocket.value.readyState === WebSocket.OPEN) {
        webSocket.value.send(JSON.stringify({ type: 'ping' }))
        console.log('➡️ 发送心跳 ping')
      }
    }, heartbeatInterval)
  }

  /**
   * 清除无消息定时器
   */
  const clearInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer)
      inactivityTimer = null
    }
  }

  /**
   * 重置无消息定时器
   */
  const resetInactivityTimer = () => {
    clearInactivityTimer()
    inactivityTimer = setTimeout(() => {
      console.log('无消息超时，关闭连接')
      close()
      callbacks.onMessage?.({ type: 'inactivity_timeout' })
    }, inactivityTimeout)
  }

  /**
   * 连接成功事件
   */
  const onOpen = () => {
    console.log('WebSocket 连接成功')
    reconnectTime.value = 0
    resetInactivityTimer()
    startHeartbeat()
    callbacks.onOpen?.()
  }

  /**
   * 连接错误事件
   */
  const onError = (event: Event) => {
    console.log('WebSocket 连接错误', event)
    callbacks.onError?.(event)
  }

  /**
   * 连接关闭事件
   */
  const onClose = (event: CloseEvent) => {
    console.log('WebSocket 连接关闭', event)
    stopHeartbeat()
    clearInactivityTimer()
    callbacks.onClose?.(event)

    // 原始逻辑：onClose 中不自动重连，重连逻辑在 reconnect() 函数中手动调用
    // 这里保持与原始代码一致，不自动触发重连
    // 如果需要重连，应该在 onError 或其他地方手动调用 reconnect()
  }

  /**
   * 接收消息事件
   */
  const onMessage = (msgEvent: MessageEvent) => {
    try {
      const data = JSON.parse(msgEvent.data)
      resetInactivityTimer() // 每次收到消息就重置计时
      callbacks.onMessage?.(data)
    } catch (error) {
      console.error('解析 WebSocket 消息失败:', error)
    }
  }

  /**
   * 初始化 WebSocket 连接
   */
  const initWebSocket = () => {
    if (webSocket.value?.readyState === WebSocket.OPEN) {
      console.log('WebSocket 已连接，跳过初始化')
      return
    }

    // 使用 toValue() 获取响应式或非响应式值的当前值
    const currentDid = toValue(did)
    const currentUri = toValue(uri)

    // 如果 did 未提供，无法建立连接
    if (!currentDid) {
      console.error('WebSocket 初始化失败: did 参数未提供')
      callbacks.onError?.(new Event('missing_did'))
      return
    }

    const host = currentUri || import.meta.env.VITE_WS_URL
    if (!host) {
      console.error('WebSocket 初始化失败: uri 和 VITE_WS_URL 都未提供')
      callbacks.onError?.(new Event('missing_uri'))
      return
    }

    const prefix = 'wss' // 可以根据需要动态判断 ws/wss
    const token = getToken()
    const wsUri = `${prefix}://${host}/${currentDid}?token=${token}`

    console.log('初始化 WebSocket 连接:', wsUri)

    try {
      webSocket.value = new WebSocket(wsUri)
      webSocket.value.onopen = onOpen
      webSocket.value.onerror = onError
      webSocket.value.onmessage = onMessage
      webSocket.value.onclose = onClose
    } catch (error) {
      console.error('创建 WebSocket 连接失败:', error)
      callbacks.onError?.(error as Event)
    }
  }

  // 监听 uri / did 变化时关闭旧连接并用新参数重连
  const stopWatch = watch(
    () => [toValue(uri), toValue(did)] as const,
    ([newUri, newDid], [oldUri, oldDid]) => {
      if (newUri === oldUri && newDid === oldDid) return
      close()
      if (newDid && (newUri ?? import.meta.env.VITE_WS_URL)) {
        reconnectTime.value = 0
        initWebSocket()
      }
    },
    { immediate: false },
  )

  /**
   * 重连
   */
  const reconnect = () => {
    if (lockReconnect.value || !webSocket.value) {
      return
    }

    // maxReconnect 表示最大重连次数，达到即停止（使用 >= 语义直观、不越界）
    if (maxReconnect !== -1 && reconnectTime.value >= maxReconnect) {
      console.log('达到最大重连次数，停止重连')
      callbacks.onMessage?.({ type: 'max_reconnect_reached' })
      return
    }

    lockReconnect.value = true
    const currentAttempt = reconnectTime.value + 1
    setTimeout(() => {
      try {
        reconnectTime.value = currentAttempt
        console.log(`尝试重连 (${currentAttempt}/${maxReconnect === -1 ? '∞' : maxReconnect})`)
        initWebSocket()
      } finally {
        // 无论 initWebSocket 成功、抛错或内部提前 return，都释放锁，避免永久锁定导致无法重连
        lockReconnect.value = false
      }
    }, reconnectDelay)
  }

  /**
   * 关闭连接
   */
  const close = () => {
    stopHeartbeat()
    clearInactivityTimer()

    if (webSocket.value) {
      const ws = webSocket.value
      // 先移除事件监听，避免关闭过程中或平台延迟释放时仍触发回调导致泄漏或误用
      ws.onopen = null
      ws.onerror = null
      ws.onmessage = null
      ws.onclose = null
      if (ws.readyState !== WebSocket.CLOSED) {
        ws.close(1000, '主动关闭')
      }
      webSocket.value = null
    }
  }

  // 组件/作用域卸载时：先停止 watch，再关闭连接与定时器，避免监听器重复创建或未释放
  const scope = getCurrentScope()
  if (scope) {
    onScopeDispose(() => {
      stopWatch()
      close()
    })
  }

  /**
   * 发送消息
   */
  const send = (data: string | object) => {
    if (webSocket.value?.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      webSocket.value.send(message)
      return true
    } else {
      console.warn('WebSocket 未连接，无法发送消息')
      return false
    }
  }

  /**
   * 获取连接状态
   */
  const readyState = computed<WebSocketReadyState>(() => {
    return (webSocket.value?.readyState ?? WebSocket.CLOSED) as WebSocketReadyState
  })

  /**
   * 是否已连接
   */
  const isConnected = computed(() => {
    return readyState.value === WebSocket.OPEN
  })

  return {
    webSocket: readonly(webSocket),
    readyState,
    isConnected,
    reconnectTime: readonly(reconnectTime),
    initWebSocket,
    reconnect,
    close,
    send,
    startHeartbeat,
    stopHeartbeat,
  }
}
